import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function LinearSystemsWordProblemsPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        <strong>Układ równań</strong> składa się z równań zawierających te same
        niewiadome. Jego rozwiązaniem jest para liczb, która po podstawieniu
        spełnia <strong>oba równania jednocześnie</strong>.
      </p>
      <BlockMath
        math={String.raw`\begin{cases}x+y=8\\2x-y=1\end{cases}\qquad (x,y)=(3,5)`}
      />
      <p>
        Najbardziej uniwersalna jest <strong>metoda podstawiania</strong>. Z jednego
        równania wyznaczamy jedną niewiadomą, otrzymane wyrażenie podstawiamy do
        drugiego równania, a na końcu wracamy po wartość drugiej niewiadomej.
      </p>
      <ol style={{ paddingLeft: "1.4rem", marginTop: 0 }}>
        <li>Wybierz równanie, z którego najłatwiej wyznaczyć <InlineMath math="x" /> albo <InlineMath math="y" />.</li>
        <li>Podstaw otrzymane wyrażenie do drugiego równania.</li>
        <li>Rozwiąż równanie z jedną niewiadomą.</li>
        <li>Oblicz drugą niewiadomą i sprawdź wynik w obu równaniach.</li>
      </ol>
      <p>
        Geometrycznie każde równanie liniowe opisuje prostą. Dwie przecinające się proste
        oznaczają jedno rozwiązanie, różne proste równoległe — brak rozwiązań,
        a pokrywające się proste — nieskończenie wiele rozwiązań.
        Na wykresie sprawdzamy nachylenie, przecięcie z osiami i wspólny punkt.
      </p>
      <p>
        W zadaniu tekstowym najpierw zapisujemy, <strong>co oznaczają niewiadome</strong>
        i w jakich są jednostkach. Każdą niezależną informację z treści zamieniamy
        następnie na osobne równanie. Dopiero wtedy rozwiązujemy układ i zapisujemy
        odpowiedź zgodną z pytaniem.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Podstawianie w prostym układzie",
      description: "Wyznaczamy niewiadomą z prostszego równania i podstawiamy ją do drugiego.",
      content: (
        <div className="space-y-3">
          <BlockMath math={String.raw`\begin{cases}y=x+2\\2x+y=11\end{cases}`} />
          <p>
            Pierwsze równanie już podaje wartość <InlineMath math="y" />. Podstawiamy
            więc <InlineMath math="x+2" /> w miejsce <InlineMath math="y" /> w drugim równaniu:
          </p>
          <BlockMath math={String.raw`2x+(x+2)=11`} />
          <BlockMath math={String.raw`3x+2=11\quad\Longrightarrow\quad 3x=9\quad\Longrightarrow\quad x=3`} />
          <p>Wracamy do pierwszego równania:</p>
          <BlockMath math={String.raw`y=x+2=3+2=5`} />
          <p><strong>Rozwiązanie:</strong> <InlineMath math={String.raw`(x,y)=(3,5)`} />.</p>
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Od treści do układu równań",
      description: "Najpierw oznaczamy szukane liczby, a potem tłumaczymy każde zdanie na równanie.",
      content: (
        <div className="space-y-3">
          <p>Suma dwóch liczb jest równa 26, a pierwsza liczba jest o 8 większa od drugiej.</p>
          <p>
            Niech <InlineMath math="x" /> oznacza pierwszą liczbę, a <InlineMath math="y" /> drugą.
            Zapisujemy obie informacje:
          </p>
          <BlockMath math={String.raw`\begin{cases}x+y=26\\x=y+8\end{cases}`} />
          <p>Podstawiamy <InlineMath math="y+8" /> w miejsce <InlineMath math="x" />:</p>
          <BlockMath math={String.raw`y+8+y=26\quad\Longrightarrow\quad 2y=18\quad\Longrightarrow\quad y=9`} />
          <BlockMath math={String.raw`x=y+8=9+8=17`} />
          <p>
            <strong>Odpowiedź:</strong> szukane liczby to <InlineMath math="17" /> i <InlineMath math="9" />.
          </p>
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Liczba biletów i utarg",
      description: "Dwa równania opisują tę samą sprzedaż: liczbę biletów oraz ich łączną wartość.",
      content: (
        <div className="space-y-3">
          <p>
            Sprzedano łącznie 30 biletów. Bilet normalny kosztował 20 zł, ulgowy 10 zł,
            a całkowity utarg wyniósł 480 zł. Ile sprzedano biletów każdego rodzaju?
          </p>
          <p>
            Niech <InlineMath math="n" /> oznacza liczbę biletów normalnych, a <InlineMath math="u" />
            liczbę biletów ulgowych:
          </p>
          <BlockMath math={String.raw`\begin{cases}n+u=30\\20n+10u=480\end{cases}`} />
          <p>Z pierwszego równania wyznaczamy <InlineMath math="u=30-n" /> i podstawiamy:</p>
          <BlockMath math={String.raw`20n+10(30-n)=480`} />
          <BlockMath math={String.raw`20n+300-10n=480\quad\Longrightarrow\quad 10n=180\quad\Longrightarrow\quad n=18`} />
          <BlockMath math={String.raw`u=30-18=12`} />
          <p>
            <strong>Odpowiedź:</strong> sprzedano 18 biletów normalnych i 12 ulgowych.
          </p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
